import { motion } from 'framer-motion'
import { Smartphone, Zap, CheckCircle2, ArrowRight } from 'lucide-react'

export default function Download() {
  const features = [
    '无需下载安装，扫码即用',
    '支持 iOS 和 Android',
    '核心功能完全免费',
    '专业版更多高级功能',
    '数据云端同步保存',
    '隐私安全有保障',
  ]

  return (
    <section id="download" className="py-24 relative overflow-hidden">
      {/* 背景光晕 */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] opacity-15"
          style={{ background: 'radial-gradient(ellipse, rgba(59,130,246,0.5), transparent 70%)', filter: 'blur(60px)' }} />
      </div>
      <div className="absolute inset-0 grid-bg opacity-30" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* 标题 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="badge mx-auto mb-4">立即体验</div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight">
            开启你的
            <br />
            <span className="gradient-text">AI 训练之旅</span>
          </h2>
          <p className="text-lg text-gray-500">
            扫描微信小程序码，30 秒完成首次 AI 动作分析
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="rounded-3xl overflow-hidden relative"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))',
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 40px 80px rgba(0,0,0,0.4)',
            }}
          >
            {/* 顶部光线 */}
            <div className="absolute top-0 left-0 right-0 h-px"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(96,165,250,0.5), rgba(167,139,250,0.5), transparent)' }} />

            <div className="p-8 md:p-12 grid md:grid-cols-2 gap-10 items-center">
              {/* 左侧：二维码 */}
              <div className="flex flex-col items-center">
                <motion.div
                  whileHover={{ scale: 1.03 }}
                  transition={{ duration: 0.3 }}
                  className="relative w-52 h-52 rounded-2xl overflow-hidden mb-5"
                  style={{
                    background: 'linear-gradient(135deg, #1e1e3a, #0d0d1e)',
                    border: '1px solid rgba(96,165,250,0.2)',
                    boxShadow: '0 0 40px rgba(59,130,246,0.1)',
                  }}
                >
                  {/* 四角装饰 */}
                  {['top-2 left-2', 'top-2 right-2', 'bottom-2 left-2', 'bottom-2 right-2'].map((pos, i) => (
                    <div key={i}
                      className={`absolute ${pos} w-5 h-5`}
                      style={{
                        borderTop: i < 2 ? '2px solid #60a5fa' : 'none',
                        borderBottom: i >= 2 ? '2px solid #60a5fa' : 'none',
                        borderLeft: i % 2 === 0 ? '2px solid #60a5fa' : 'none',
                        borderRight: i % 2 === 1 ? '2px solid #60a5fa' : 'none',
                        borderRadius: i === 0 ? '4px 0 0 0' : i === 1 ? '0 4px 0 0' : i === 2 ? '0 0 0 4px' : '0 0 4px 0',
                      }}
                    />
                  ))}

                    {/* 真实小程序码 */}
                  <img
                    src="/images/miniapp-code.jpg"
                    alt="Lafit智拍AI网球教练小程序码"
                    className="absolute inset-0 w-full h-full object-cover"
                  />

                  {/* 扫描动效 */}
                  <motion.div
                    className="absolute left-0 right-0 h-0.5"
                    style={{ background: 'linear-gradient(90deg, transparent, rgba(96,165,250,0.8), transparent)' }}
                    animate={{ top: ['10%', '90%', '10%'] }}
                    transition={{ duration: 2.5, repeat: Infinity, ease: 'linear' }}
                  />
                </motion.div>

                <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                  <Smartphone className="w-4 h-4 text-blue-400" />
                  微信扫一扫 · 立即体验
                </div>
              </div>

              {/* 右侧：信息 */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, #2563eb, #7c3aed)', boxShadow: '0 0 20px rgba(59,130,246,0.3)' }}>
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Lafit智拍AI网球教练</h3>
                    <p className="text-xs text-gray-500">微信小程序 · 免费使用</p>
                  </div>
                </div>

                <div className="space-y-2.5 mb-8">
                  {features.map((f) => (
                    <div key={f} className="flex items-center gap-2.5">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                      <span className="text-sm text-gray-400">{f}</span>
                    </div>
                  ))}
                </div>

                <a
                  href="#download"
                  className="btn-primary flex items-center justify-center gap-2 w-full mb-4"
                >
                  <span className="flex items-center gap-2">
                    扫码立即体验
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </a>

                <p className="text-xs text-center text-gray-600">
                  已有{' '}
                  <span className="font-bold text-blue-400">10,000+</span>{' '}
                  用户正在使用
                </p>
              </div>
            </div>
          </motion.div>

          {/* 特性标签行 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8"
          >
            {[
              { label: '实时 AI 分析', color: '#60a5fa' },
              { label: '专业动作库', color: '#a78bfa' },
              { label: '个性化训练', color: '#34d399' },
              { label: '进度追踪', color: '#fbbf24' },
            ].map((item) => (
              <div
                key={item.label}
                className="py-3 rounded-xl text-center text-sm font-medium transition-all hover:-translate-y-0.5 cursor-default"
                style={{
                  background: `${item.color}08`,
                  border: `1px solid ${item.color}22`,
                  color: item.color,
                }}
              >
                ✓ {item.label}
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  )
}
